import api from '@Services/api';
import { AxiosResponse } from 'axios';
import {
	UpdateWhatsappProviderPayload,
	WhatsappCatalog,
	WhatsappProviderName,
	WhatsappSettingsResponse,
	WhatsappWebhookRegistration,
} from './whatsapp.types';

/**
 * Settings -> WhatsApp. Admin-only endpoints; secrets are write-only
 * (GET/PUT responses mask them as {has_value, hint}). catalog/webhook go
 * through the backend to the provider's management API — the API key never
 * leaves the server once stored, and a typed-but-unsaved key travels only in
 * a POST body.
 */
export class WhatsappSettingsRequester {
	private static instance: WhatsappSettingsRequester;
	static getInstance() {
		if (!WhatsappSettingsRequester.instance) {
			WhatsappSettingsRequester.instance = new WhatsappSettingsRequester();
		}
		return WhatsappSettingsRequester.instance;
	}
	baseURL = 'settings/whatsapp';

	async getAll(): Promise<AxiosResponse<ApiResponse<WhatsappSettingsResponse>>> {
		return await api.get(`${this.baseURL}`);
	}

	async update(
		payload: UpdateWhatsappProviderPayload
	): Promise<AxiosResponse<ApiResponse<WhatsappSettingsResponse>>> {
		return await api.put(`${this.baseURL}`, payload);
	}

	async catalog(payload: {
		provider: WhatsappProviderName;
		api_key?: string | null;
	}): Promise<AxiosResponse<ApiResponse<WhatsappCatalog>>> {
		return await api.post(`${this.baseURL}/catalog`, payload);
	}

	async registerWebhook(payload: {
		provider: WhatsappProviderName;
		api_key?: string | null;
		base_url: string;
		/** 'exam' (student API, default) or 'attendance' (admin API). */
		target?: 'exam' | 'attendance';
	}): Promise<AxiosResponse<ApiResponse<WhatsappWebhookRegistration>>> {
		return await api.post(`${this.baseURL}/webhook`, payload);
	}

	async failedCount(
		hours: number
	): Promise<AxiosResponse<ApiResponse<{ hours: number; failed: number }>>> {
		return await api.get(`${this.baseURL}/failed-count`, { params: { hours } });
	}

	async retryFailed(
		hours: number
	): Promise<AxiosResponse<ApiResponse<{ hours: number; requeued: number }>>> {
		return await api.post(`${this.baseURL}/retry-failed`, { hours });
	}

	/** Lift the dispatcher's circuit-breaker pause now instead of waiting for its next probe. */
	async resume(
		provider: WhatsappProviderName
	): Promise<AxiosResponse<ApiResponse<WhatsappSettingsResponse>>> {
		return await api.post(`${this.baseURL}/resume`, { provider });
	}
}

export const whatsappErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ??
	error?.response?.data?.errors?.[0]?.message ??
	fallback;
